import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { sharingService } from '../../services/sharingService';
import { CircularProgress, Box, Typography } from '@mui/material';

const PostAuthRedirect: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, appAccess } = useAuth();
  const hasProcessedRedirect = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Only run this effect when authentication is complete and user is authenticated
    // AND we haven't processed a redirect yet
    // AND we're not on the share page or any app page (to avoid interfering with navigation)
    // Use dynamic check based on user's accessible apps instead of hardcoded patterns
    const isOnAppPage = appAccess?.accessible_apps.some(app => 
      window.location.pathname === app.app_path
    ) ?? false;
    
    if (!loading && isAuthenticated && !hasProcessedRedirect.current && 
        window.location.pathname !== '/share' && !isOnAppPage) {
      
      const handlePostAuthRedirect = () => {
        // Check for pending shared data in localStorage first
        const pendingSharedData = localStorage.getItem('pendingSharedData');
        const pendingTimestamp = localStorage.getItem('pendingSharedDataTimestamp');
        const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');

        // Clean up old data (older than 1 hour)
        if (pendingTimestamp) {
          const timestamp = parseInt(pendingTimestamp, 10);
          const oneHourAgo = Date.now() - (60 * 60 * 1000);
          if (timestamp < oneHourAgo) {
            localStorage.removeItem('pendingSharedData');
            localStorage.removeItem('pendingSharedDataTimestamp');
          }
        }

        if (pendingSharedData) {
          setIsProcessing(true);
          
          // Clear the stored data
          localStorage.removeItem('pendingSharedData');
          localStorage.removeItem('pendingSharedDataTimestamp');
          sessionStorage.removeItem('redirectAfterLogin');

          // Process the shared data
          try {
            // Parse the shared data from the stored parameter
            const sharedData = sharingService.decodeData(pendingSharedData);
            if (sharedData) {
              // Determine the target app based on the type
              let targetPath = '/';
              
              switch (sharedData.type) {
                case 'flooded_evaporator_rating':
                case 'flooded_evaporator_selection':
                  targetPath = '/flooded-evaporator';
                  break;
                case 'dry_expansion_evaporator_rating':
                case 'dry_expansion_evaporator_selection':
                  targetPath = '/dry-expansion-evaporator';
                  break;
                case 'condenser_rating':
                case 'condenser_selection':
                  targetPath = '/condenser';
                  break;
                case 'special_flooded_evaporator':
                  targetPath = '/special-flooded-evaporator';
                  break;
                case 'special_condenser':
                  targetPath = '/special-condenser';
                  break;
                case 'coaxial_evaporator':
                  targetPath = '/coaxial-evaporator';
                  break;
                case 'coaxial_condenser':
                  targetPath = '/coaxial-condenser';
                  break;
                case 'cold_plate':
                  targetPath = '/cold-plate';
                  break;
                case 'special_flooded_evaporator_ai':
                  targetPath = '/special-flooded-evaporator-ai';
                  break;
                default:
                  console.error(`PostAuthRedirect: Unknown app type: ${sharedData.type}`);
                  navigate('/');
                  hasProcessedRedirect.current = true;
                  setIsProcessing(false);
                  return;
              }

              // Navigate to the target app with the shared data
              navigate(targetPath, {
                state: {
                  importedData: sharedData
                },
                replace: true
              });
              hasProcessedRedirect.current = true;
              setIsProcessing(false);
              return;
            }
          } catch (error) {
            console.error('PostAuthRedirect: Error processing pending shared data:', error);
            setIsProcessing(false);
          }
        }

        // Handle regular redirect after login
        if (redirectAfterLogin) {
          sessionStorage.removeItem('redirectAfterLogin');
          
          // Don't redirect to login page if that's where we came from
          if (redirectAfterLogin !== '/login') {
            navigate(redirectAfterLogin, { replace: true });
            hasProcessedRedirect.current = true;
            return;
          }
        }

        // Only redirect to home if we're currently on the login page
        // This prevents interfering with normal navigation
        if (window.location.pathname === '/login') {
          navigate('/', { replace: true });
        }
        
        hasProcessedRedirect.current = true;
      };

      // Small delay to ensure all auth state is properly set
      const timeoutId = setTimeout(handlePostAuthRedirect, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, loading, navigate, appAccess?.accessible_apps]);

  // Only show loading when actually processing a redirect
  if (isProcessing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading shared data...
        </Typography>
      </Box>
    );
  }

  return null;
};

export default PostAuthRedirect;